import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
  getInvoicePdfLink,
  getInvoiceOptions,
  listTpInvoices,
  sendInvoiceEmail,
  updateMoloniInvoice,
  getMoloniInvoice,
  getInvoiceEmailBody,
  getMoloniCustomerDetails,
} from "../../services/invoicingServices";
import StockAlertsBanner from "./StockAlertsBanner";

const Faturacao = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // noProvider: empresa sem ligação a software de facturação activa.
  // Quando true, esconde a listagem e mostra alerta a redireccionar
  // para Definições. providerMsg pode trazer mensagem do backend.
  const [noProvider, setNoProvider] = useState(false);
  const [providerMsg, setProviderMsg] = useState("");
  const [providerProbed, setProviderProbed] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all | draft | final
  const [typeFilter, setTypeFilter] = useState("all"); // all | FT | FR | FS
  const [busyRow, setBusyRow] = useState(null);
  const [search, setSearch] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advFilters, setAdvFilters] = useState({
    dateFrom: "",
    dateTo: "",
    customerVat: "",
    minTotal: "",
    maxTotal: "",
  });

  // Email modal
  const [emailModal, setEmailModal] = useState({ open: false, row: null });
  const [emailForm, setEmailForm] = useState({
    email: "",
    subject: "",
    message: "",
    cc: "",
  });
  const [emailSending, setEmailSending] = useState(false);

  const probeProvider = async () => {
    // Probe a `/moloni/invoice-options`. Falha (`status!=200` ou `data`
    // vazio) → empresa sem mapping Moloni activo. Mostramos alerta em
    // vez de listagem vazia ambígua.
    try {
      const r = await getInvoiceOptions();
      const data = r?.data?.data;
      const ok =
        r?.data?.status === 200 &&
        data &&
        (data.document_sets ||
          data.payment_methods ||
          data.invoice_document_types);
      if (!ok) {
        setNoProvider(true);
        setProviderMsg(r?.data?.message || "");
        return false;
      }
      setNoProvider(false);
      setProviderMsg("");
      return true;
    } catch (err) {
      setNoProvider(true);
      setProviderMsg(
        err?.response?.data?.message ||
          "Sem ligação a software de facturação."
      );
      return false;
    } finally {
      setProviderProbed(true);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      // 1. Probe ligação a software de facturação
      const hasProvider = await probeProvider();
      if (!hasProvider) {
        setRows([]);
        return;
      }
      // 2. Listagem normal
      const params = { limit: 200 };
      if (statusFilter === "draft") params.status = 0;
      if (statusFilter === "final") params.status = 1;
      if (statusFilter === "canceled") params.status = 2;
      if (typeFilter !== "all") params.saft_code = typeFilter;
      if (search.trim()) params.search = search.trim();
      if (advFilters.dateFrom) params.date_from = advFilters.dateFrom;
      if (advFilters.dateTo) params.date_to = advFilters.dateTo;
      if (advFilters.customerVat) params.customer_vat = advFilters.customerVat;
      if (advFilters.minTotal) params.min_total = advFilters.minTotal;
      if (advFilters.maxTotal) params.max_total = advFilters.maxTotal;
      const resp = await listTpInvoices(params);
      const payload = resp?.data?.data || {};
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.rows)
        ? payload.rows
        : [];
      setRows(list);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter]);

  // Debounce do search e filtros avançados
  useEffect(() => {
    const id = setTimeout(load, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, advFilters]);

  const formatEur = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR",
    }).format(n);
  };

  const typeBadgeColor = (t) => {
    if (t === "FT") return { bg: "#0f7c2a", fg: "#fff" };
    if (t === "FR") return { bg: "#0d6efd", fg: "#fff" };
    if (t === "FS") return { bg: "#6c757d", fg: "#fff" };
    return { bg: "#e9ecef", fg: "#212529" };
  };

  const finalizeDraft = async (row) => {
    if (!row.document_id) return;
    const ok = window.confirm(
      t("confirm_finalize", {
        defaultValue:
          "Converter este rascunho em factura FINAL? Documento contabilístico irreversível. Se a série ainda não estiver comunicada à AT, mantém-se como rascunho.",
      }),
    );
    if (!ok) return;
    try {
      setBusyRow(row.document_id);
      // Precisamos dos produtos/linhas para o update — a Moloni exige
      // produtos[] em /invoices/update/. Vamos buscar o invoice completo.
      const detailResp = await getMoloniInvoice(row.document_id);
      const details = detailResp?.data?.data || {};
      const products = (details.products || []).map((p) => ({
        product_id: p.product_id,
        name: p.name,
        summary: p.summary,
        qty: Number(p.qty),
        price: Number(p.price),
        discount: p.discount,
        exemption_reason: p.exemption_reason,
        taxes: (p.taxes || []).map((tx) => ({
          tax_id: tx.tax_id,
          value: tx.value,
          order: tx.order,
          cumulative: tx.cumulative,
        })),
      }));
      const payload = {
        customer_id: details.customer_id || row.customer_id,
        document_set_id: details.document_set_id || row.document_set_id,
        document_type_id: details.document_type_id || row.document_type_id,
        date: (details.date || row.date || "").slice(0, 10),
        expiration_date: (details.expiration_date || row.expiration_date || "").slice(0, 10),
        our_reference: details.our_reference ?? row.our_reference ?? "",
        your_reference: details.your_reference ?? row.your_reference ?? "",
        notes: details.notes ?? "",
        payment_method_id: details.payment_method_id,
        maturity_date_id: details.maturity_date_id,
        salesman_id: details.salesman_id,
        language_id: details.language_id,
        products,
      };
      const resp = await updateMoloniInvoice(row.document_id, payload, {
        status: 1,
      });
      const data = resp?.data?.data || {};
      if (Number(data.status) === 1) {
        toast.success(
          resp?.data?.message ||
            t("invoice_final_ok", { defaultValue: "Factura final emitida" }),
        );
      } else if (data.fallback_used) {
        toast.warning(
          resp?.data?.message ||
            t("invoice_fallback", {
              defaultValue:
                "Série sem comunicação AT — mantido como rascunho. Comunica a série no portal Moloni.",
            }),
          { autoClose: 8000 },
        );
      } else {
        toast.success(resp?.data?.message || "Actualizado");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setBusyRow(null);
    }
  };

  const openPdf = async (row) => {
    if (!row.document_id) {
      toast.error(
        t("missing_moloni_id", {
          defaultValue:
            "Este rascunho não tem ID Moloni gravado. Reemite a factura.",
        }),
      );
      return;
    }
    try {
      setBusyRow(row.document_id);
      const resp = await getInvoicePdfLink(row.document_id, {
        signed: Number(row.status) === 1 ? 1 : 0,
      });
      const url = resp?.data?.data?.url;
      if (!url) {
        toast.error(
          t("pdf_unavailable", {
            defaultValue: "PDF indisponível. Tenta novamente.",
          }),
        );
        return;
      }
      window.open(url, "_blank", "noopener");
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setBusyRow(null);
    }
  };

  const openEmail = async (row) => {
    if (!row.document_id) {
      toast.error(
        t("missing_moloni_id", {
          defaultValue:
            "Este rascunho não tem ID Moloni gravado. Reemite a factura.",
        }),
      );
      return;
    }
    // Pré-popula a mensagem: settings.invoice_email_body (custom da empresa)
    // OU default localizado pelo idioma do user. Falha silenciosamente para
    // string vazia se o endpoint não responder.
    let prefilledMessage = "";
    try {
      const r = await getInvoiceEmailBody();
      const data = r?.data?.data || {};
      prefilledMessage = data.body || data.default_body || "";
    } catch (_) {}

    // Email destinatário: tenta o que veio na lista; se vazio e tem
    // customer_id, vai à Moloni buscar a ficha (caso o m_customer
    // local não tenha o email sincronizado).
    let toEmail =
      row.display_email || row.customer_email || row.entity_email || "";
    if (!toEmail && row.customer_id) {
      try {
        const r = await getMoloniCustomerDetails(row.customer_id);
        toEmail = r?.data?.data?.email || r?.data?.data?.customer?.email || "";
      } catch (_) {}
    }

    // Assunto: "<Tipo> <Série>/<Número>" — ex: "Fatura TP2026/2".
    // Para rascunhos (sem nº fiscal) usamos placeholder.
    const typeShort = (row.display_type || "").toUpperCase();
    const typeLabel =
      typeShort === "FR"
        ? "Fatura-Recibo"
        : typeShort === "FS"
        ? "Fatura Simplificada"
        : "Fatura";
    // O backend já compõe `display_number_with_series` (ex: "TP2026/2");
    // se faltar (ex: rascunho ou row antiga), recompõe localmente com "/".
    let docRef = row.display_number_with_series || "";
    if (!docRef) {
      const series = (row.document_set_name || "").trim();
      const num = String(row.number || "");
      if (num) {
        docRef = !series
          ? num
          : num.toUpperCase().startsWith(series.toUpperCase())
          ? num
          : num.includes("/")
          ? `${series}${num}`
          : `${series}/${num}`;
      }
    }
    const subject =
      Number(row.status) === 1
        ? `${typeLabel} ${docRef}`.replace(/\s+/g, " ").trim()
        : `${typeLabel} (rascunho)`;

    setEmailForm({
      email: toEmail,
      subject,
      message: prefilledMessage,
      cc: "",
    });
    setEmailModal({ open: true, row });
  };

  const closeEmail = () => {
    if (emailSending) return;
    setEmailModal({ open: false, row: null });
  };

  const sendEmail = async () => {
    const row = emailModal.row;
    if (!row) return;
    if (!emailForm.email || !/\S+@\S+\.\S+/.test(emailForm.email)) {
      toast.error(
        t("invalid_email", { defaultValue: "Email do destinatário inválido" }),
      );
      return;
    }
    try {
      setEmailSending(true);
      const resp = await sendInvoiceEmail(row.document_id, {
        email: emailForm.email.trim(),
        subject: emailForm.subject || undefined,
        message: emailForm.message || undefined,
        cc: emailForm.cc || undefined,
        attached_hash_files: Number(row.status) === 1 ? 1 : 0,
      });
      toast.success(
        resp?.data?.message ||
          t("email_sent", { defaultValue: "Email enviado pela Moloni" }),
      );
      setEmailModal({ open: false, row: null });
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setEmailSending(false);
    }
  };

  // Empresa sem ligação a software de facturação activa — bloqueamos
  // a UI normal e mostramos só o alerta a redireccionar para Definições.
  if (providerProbed && noProvider) {
    return (
      <div className="content-wrapper">
        <section className="content-header">
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ padding: "10px 20px" }}
          >
            <h3 style={{ margin: 0 }}>
              {t("invoicing", { defaultValue: "Faturação" })}
            </h3>
          </div>
        </section>
        <section className="content" style={{ padding: 20 }}>
          <div
            className="alert alert-warning d-flex flex-column align-items-start"
            role="alert"
            style={{ gap: 10, padding: 24 }}
          >
            <div className="d-flex align-items-start" style={{ gap: 12 }}>
              <i
                className="bi bi-plug"
                style={{ fontSize: 28, color: "#856404" }}
              ></i>
              <div>
                <h5 style={{ marginBottom: 6 }}>
                  Sem ligação a software de facturação
                </h5>
                <p style={{ marginBottom: 0 }}>
                  Para emitir e gerir facturas, primeiro tens de associar
                  esta empresa a um software de facturação (ex: Moloni)
                  na página de Definições.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate("/settings")}
              style={{ marginTop: 6 }}
            >
              <i className="bi bi-gear-fill me-1"></i> Ir para Definições
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div
          className="d-flex justify-content-between align-items-center"
          style={{ padding: "10px 20px" }}
        >
          <h3 style={{ margin: 0 }}>
            {t("invoicing", { defaultValue: "Faturação" })}
          </h3>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/faturacao/nova")}
          >
            <i className="bi bi-plus-lg"></i>{" "}
            {t("invoicing_new", { defaultValue: "Nova factura" })}
          </button>
        </div>
      </section>

      <section className="content" style={{ padding: 20 }}>
        <StockAlertsBanner />

        {/* Linha 1: pesquisa rápida + toggle avançado */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ position: "relative", flex: "1 1 320px", maxWidth: 480 }}>
            <i
              className="bi bi-search"
              style={{
                position: "absolute",
                top: 9,
                left: 10,
                color: "#999",
                fontSize: 13,
              }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar por nº, cliente, NIF, ref..."
              className="form-control form-control-sm"
              style={{ paddingLeft: 32 }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                style={{
                  position: "absolute",
                  right: 6,
                  top: 4,
                  border: "none",
                  background: "transparent",
                  color: "#666",
                  cursor: "pointer",
                }}
                title="Limpar"
              >
                <i className="bi bi-x-circle" />
              </button>
            )}
          </div>
          <button
            type="button"
            className={`btn btn-sm ${
              showAdvanced ? "btn-primary" : "btn-outline-secondary"
            }`}
            onClick={() => setShowAdvanced((v) => !v)}
          >
            <i className="bi bi-funnel"></i> Filtros avançados
          </button>
          <span style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>
            {rows.length} resultado{rows.length === 1 ? "" : "s"}
          </span>
        </div>

        {/* Linha 2: filtro por tipo */}
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: 10,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "#666", marginRight: 4 }}>
            Tipo:
          </span>
          {[
            { k: "all", label: "Todos" },
            { k: "FT", label: "Fatura (FT)" },
            { k: "FR", label: "Fatura-Recibo (FR)" },
            { k: "FS", label: "Fatura Simplificada (FS)" },
          ].map((b) => (
            <button
              key={b.k}
              type="button"
              className={`btn btn-sm ${
                typeFilter === b.k ? "btn-success" : "btn-outline-success"
              }`}
              onClick={() => setTypeFilter(b.k)}
            >
              {b.label}
            </button>
          ))}
        </div>

        {/* Linha 3: filtro por estado */}
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "#666", marginRight: 4 }}>
            Estado:
          </span>
          {[
            { k: "all", label: t("all", { defaultValue: "Todas" }) },
            { k: "draft", label: t("draft", { defaultValue: "Rascunho" }) },
            { k: "final", label: t("final", { defaultValue: "Final" }) },
            { k: "canceled", label: t("canceled", { defaultValue: "Cancelada" }) },
          ].map((b) => (
            <button
              key={b.k}
              type="button"
              className={`btn btn-sm ${
                statusFilter === b.k ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setStatusFilter(b.k)}
            >
              {b.label}
            </button>
          ))}
        </div>

        {/* Filtros avançados (collapse) */}
        {showAdvanced && (
          <div
            style={{
              border: "1px solid #e0e3eb",
              borderRadius: 8,
              padding: 12,
              marginBottom: 15,
              background: "#fafbfc",
            }}
          >
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              <div style={{ flex: "1 1 180px" }}>
                <label
                  style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 2 }}
                >
                  Data desde
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={advFilters.dateFrom}
                  onChange={(e) =>
                    setAdvFilters((p) => ({ ...p, dateFrom: e.target.value }))
                  }
                />
              </div>
              <div style={{ flex: "1 1 180px" }}>
                <label
                  style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 2 }}
                >
                  Data até
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={advFilters.dateTo}
                  onChange={(e) =>
                    setAdvFilters((p) => ({ ...p, dateTo: e.target.value }))
                  }
                />
              </div>
              <div style={{ flex: "1 1 200px" }}>
                <label
                  style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 2 }}
                >
                  NIF cliente
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={advFilters.customerVat}
                  onChange={(e) =>
                    setAdvFilters((p) => ({ ...p, customerVat: e.target.value }))
                  }
                  placeholder="500000000"
                />
              </div>
              <div style={{ flex: "1 1 130px" }}>
                <label
                  style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 2 }}
                >
                  Total mín. (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control form-control-sm"
                  value={advFilters.minTotal}
                  onChange={(e) =>
                    setAdvFilters((p) => ({ ...p, minTotal: e.target.value }))
                  }
                />
              </div>
              <div style={{ flex: "1 1 130px" }}>
                <label
                  style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 2 }}
                >
                  Total máx. (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control form-control-sm"
                  value={advFilters.maxTotal}
                  onChange={(e) =>
                    setAdvFilters((p) => ({ ...p, maxTotal: e.target.value }))
                  }
                />
              </div>
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() =>
                    setAdvFilters({
                      dateFrom: "",
                      dateTo: "",
                      customerVat: "",
                      minTotal: "",
                      maxTotal: "",
                    })
                  }
                >
                  Limpar
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && <div>{t("loading", { defaultValue: "A carregar..." })}</div>}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="text-muted" style={{ padding: 30, textAlign: "center" }}>
            {t("invoicing_empty", {
              defaultValue: "Sem facturas. Cria uma nova.",
            })}
          </div>
        )}

        {!loading && rows.length > 0 && (
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th style={{ width: 70 }}>Tipo</th>
                <th>Documento</th>
                <th style={{ width: 100 }}>{t("date", { defaultValue: "Data" })}</th>
                <th>{t("customer", { defaultValue: "Cliente" })}</th>
                <th>{t("document_set", { defaultValue: "Série" })}</th>
                <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  Sem IVA
                </th>
                <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  Total c/IVA
                </th>
                <th style={{ width: 90 }}>
                  {t("status", { defaultValue: "Estado" })}
                </th>
                <th style={{ textAlign: "right" }}>
                  {t("actions", { defaultValue: "Acções" })}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const hasMoloniId = !!r.document_id;
                const isBusy = hasMoloniId && busyRow === r.document_id;
                const statusNum = Number(r.status);
                const isFinal = statusNum === 1;
                const isCanceled = statusNum === 2;
                const isDraft = !isFinal && !isCanceled;
                const customerDisplay =
                  r.display_customer ||
                  r.customer_name ||
                  r.entity_name ||
                  (r.display_vat ? `NIF ${r.display_vat}` : r.customer_id);
                const typeColor = typeBadgeColor(r.display_type);
                return (
                  <tr
                    key={r.id || r.document_id}
                    style={
                      isCanceled
                        ? {
                            background: "#fff5f5",
                            color: "#7a1a1a",
                            textDecoration: "line-through",
                            textDecorationColor: "rgba(220,53,69,0.45)",
                          }
                        : undefined
                    }
                  >
                    <td>
                      <span
                        className="badge"
                        style={{
                          background: typeColor.bg,
                          color: typeColor.fg,
                          fontSize: 11,
                          padding: "4px 8px",
                        }}
                        title={r.document_type_title || r.display_type}
                      >
                        {r.display_type || "—"}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {r.display_number_with_series ||
                          r.number ||
                          (isCanceled ? (
                            <span className="text-muted">— cancelada —</span>
                          ) : isFinal ? (
                            <span className="text-muted">— sem nº —</span>
                          ) : (
                            <span className="text-muted">— rascunho —</span>
                          ))}
                      </div>
                    </td>
                    <td>{(r.date || "").slice(0, 10)}</td>
                    <td>
                      <div>{customerDisplay}</div>
                      {r.display_vat && (
                        <small className="text-muted">NIF {r.display_vat}</small>
                      )}
                    </td>
                    <td>{r.document_set_name || r.document_set_id || "—"}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {formatEur(r.net_value)}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        whiteSpace: "nowrap",
                        fontWeight: 600,
                      }}
                    >
                      {formatEur(
                        r.gross_value != null
                          ? r.gross_value
                          : (Number(r.net_value || 0) +
                              Number(r.taxes_value || 0)),
                      )}
                    </td>
                    <td>
                      {isCanceled ? (
                        <span className="badge bg-danger">
                          <i className="bi bi-x-circle me-1" />
                          {t("canceled", { defaultValue: "Cancelada" })}
                        </span>
                      ) : isFinal ? (
                        <span className="badge bg-success">
                          {t("final", { defaultValue: "Final" })}
                        </span>
                      ) : (
                        <span className="badge bg-warning text-dark">
                          {t("draft", { defaultValue: "Rascunho" })}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-1"
                        onClick={() => openPdf(r)}
                        disabled={isBusy || !hasMoloniId || !isFinal}
                        title={
                          !hasMoloniId
                            ? "Sem ID Moloni"
                            : !isFinal
                            ? "PDF disponível só após emissão final (Moloni não gera PDF para rascunhos)"
                            : "Ver PDF"
                        }
                      >
                        {isBusy ? (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                          />
                        ) : (
                          <i className="bi bi-file-earmark-pdf"></i>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary me-1"
                        onClick={() => openEmail(r)}
                        disabled={!hasMoloniId || !isFinal}
                        title={
                          !hasMoloniId
                            ? "Sem ID Moloni"
                            : !isFinal
                            ? "Email disponível só após emissão final (depende do PDF)"
                            : "Enviar por email"
                        }
                      >
                        <i className="bi bi-envelope"></i>
                      </button>
                      {isDraft && hasMoloniId && (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-warning me-1"
                            onClick={() =>
                              navigate(`/faturacao/editar/${r.document_id}`)
                            }
                            title={t("edit_draft", { defaultValue: "Editar" })}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => finalizeDraft(r)}
                            disabled={isBusy}
                            title={t("finalize", {
                              defaultValue: "Converter em final",
                            })}
                          >
                            <i className="bi bi-check-circle"></i>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <Modal isOpen={emailModal.open} toggle={closeEmail} size="lg">
        <ModalHeader toggle={closeEmail}>
          {t("send_invoice_by_email", {
            defaultValue: "Enviar factura por email",
          })}
        </ModalHeader>
        <ModalBody>
          {emailModal.row && (
            <div
              style={{
                marginBottom: 10,
                color: "#6c757d",
                fontSize: 13,
              }}
            >
              {t("document", { defaultValue: "Documento" })}:{" "}
              <strong>
                {emailModal.row.display_number ||
                  emailModal.row.display_number_with_series ||
                  emailModal.row.document_set_name ||
                  emailModal.row.document_set_id}
              </strong>
              {(emailModal.row.display_customer ||
                emailModal.row.customer_name) && (
                <>
                  {" · "}
                  {emailModal.row.display_customer ||
                    emailModal.row.customer_name}
                </>
              )}
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">
              {t("to_email", { defaultValue: "Para (email)" })} *
            </label>
            <input
              type="email"
              className="form-control"
              value={emailForm.email}
              onChange={(e) =>
                setEmailForm((p) => ({ ...p, email: e.target.value }))
              }
              placeholder="cliente@empresa.pt"
            />
            <small className="text-muted">
              {t("multiple_emails_hint", {
                defaultValue: "Para múltiplos, separa com ;",
              })}
            </small>
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("cc", { defaultValue: "CC" })}
            </label>
            <input
              type="text"
              className="form-control"
              value={emailForm.cc}
              onChange={(e) =>
                setEmailForm((p) => ({ ...p, cc: e.target.value }))
              }
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("subject", { defaultValue: "Assunto" })}
            </label>
            <input
              type="text"
              className="form-control"
              value={emailForm.subject}
              onChange={(e) =>
                setEmailForm((p) => ({ ...p, subject: e.target.value }))
              }
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("message", { defaultValue: "Mensagem" })}
            </label>
            <textarea
              className="form-control"
              rows="5"
              value={emailForm.message}
              onChange={(e) =>
                setEmailForm((p) => ({ ...p, message: e.target.value }))
              }
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeEmail}
            disabled={emailSending}
          >
            {t("cancel", { defaultValue: "Cancelar" })}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={sendEmail}
            disabled={emailSending}
          >
            {emailSending ? (
              <span className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <>
                <i className="bi bi-send"></i>{" "}
                {t("send", { defaultValue: "Enviar" })}
              </>
            )}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Faturacao;
